import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type { OabCaptureStep } from '../tracking/oabCapture/oabCaptureEvents';
import { RootState } from '../../../redux/types';
import { getOabEligibility, updateProfession } from './api';
import { isInvitePostponed, postponeInvite } from './oabInvitePostponement';
import { OAB_ELIGIBILITY_QUERY_KEY } from './queryKeys';
import { OabEligibility } from './types';

const BANNER_DISPLAY_QUERY_KEY = ['oabCaptureBannerDisplay'] as const;

const TEN_MINUTES = 1000 * 60 * 10;

export function useOabEligibility({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const queryClient = useQueryClient();
  const authToken = useSelector((state: RootState) => state.auth?.authToken);
  const userId = useSelector((state: RootState) => state.auth?.user?.id);

  const queryKey = [...OAB_ELIGIBILITY_QUERY_KEY, userId] as const;

  const { data: eligibility, isError, isFetched, isFetching } = useQuery({
    queryKey,
    queryFn: getOabEligibility,
    enabled: enabled && Boolean(authToken) && userId != null,
    staleTime: TEN_MINUTES,
    retry: 1,
  });

  const {
    mutateAsync: answerProfession,
    isLoading: isAnsweringProfession,
  } = useMutation({
    mutationFn: updateProfession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Kept in the query cache, not in a ref: the dashboard routes are lazy() and
  // remount, so a ref would report one display per navigation back to it.
  // Scoped by step because the banner has two of them: the question and, after
  // it is answered, the invitation to register the OAB. One flag for both would
  // silence the invitation's impression while its dismissal still fires.
  const hasRecordedDisplay = useCallback(
    (step: OabCaptureStep) =>
      queryClient.getQueryData([...BANNER_DISPLAY_QUERY_KEY, userId, step]) ===
      true,
    [queryClient, userId],
  );

  const markDisplayRecorded = useCallback(
    (step: OabCaptureStep): void => {
      const displayKey = [...BANNER_DISPLAY_QUERY_KEY, userId, step];
      // Sem observer, essa entrada seguiria o `cacheTime` padrão (5min) e
      // seria coletada em qualquer sessão mais longa que isso, reabrindo a
      // janela de exibição e duplicando `trackOabCaptureSurfaceViewed`.
      queryClient.setQueryDefaults(displayKey, { cacheTime: Infinity });
      queryClient.setQueryData(displayKey, true);
    },
    [queryClient, userId],
  );

  // Any surface that asked closes the window for all of them: the backend derives
  // should_show_banner from the marks the surface just wrote, so the cache is only
  // catching up with the answer a refetch would bring anyway.
  const markBannerShown = useCallback((): void => {
    queryClient.setQueryData<OabEligibility>(
      [...OAB_ELIGIBILITY_QUERY_KEY, userId],
      (current) =>
        current ? { ...current, shouldShowBanner: false } : current,
    );
  }, [queryClient, userId]);

  // Adiamento do convite: dispensa própria, no device, porque o backend não tem
  // cadência para este modo (ver oabInvitePostponement).
  const postponeInviteForUser = useCallback(
    () => postponeInvite(userId),
    [userId],
  );

  // O backend zera is_eligible ao gravar a profissão, então quem se declarou
  // advogado e ainda não tem OAB só é alcançável por esta derivação (FT-948).
  const needsOabRegistration = Boolean(
    eligibility && eligibility.profession === 'LAWYER' && !eligibility.hasOab,
  );

  const isQuestionEligible = Boolean(
    eligibility?.isEligible && eligibility?.shouldShowBanner,
  );

  return {
    answerProfession,
    isAnsweringProfession,
    isError,
    // Diz que a consulta assentou (sucesso ou falha), não só que ela falhou —
    // quem arma a captura de valor precisa saber quando parar de esperar
    // para decidir "não elegível" em vez de julgar cedo demais uma resposta
    // ainda em voo.
    // `isFetching` entra porque um refetch (v4) mantém `isFetched` e o dado
    // anterior: sem ele, uma invalidação (cadastro de OAB em outro fluxo)
    // deixaria a decisão sair pela elegibilidade velha.
    isEligibilityResolved: enabled && isFetched && !isFetching,
    needsOabRegistration,
    isQuestionEligible,
    // O convite de cadastro é um estado, não uma reexibição periódica: quem
    // respondeu advogado já queimou o `should_show_banner` da própria sessão
    // (o intervalo mínimo do M1 conta do último `lastShownAt`, gravado na
    // exibição da pergunta segundos antes). Gatear o convite nele o tornaria
    // inalcançável. Quem adia é o × do banner.
    // `enabled` faz parte do gate: com a query desabilitada, `useQuery` (v4)
    // pode devolver dado velho do cache em vez de `undefined`, e sem checar
    // `enabled` aqui o banner ficaria elegível com uma elegibilidade stale.
    isBannerEligible: enabled && (isQuestionEligible || needsOabRegistration),
    isInvitePostponed: isInvitePostponed(userId),
    postponeInvite: postponeInviteForUser,
    markBannerShown,
    hasRecordedDisplay,
    markDisplayRecorded,
  };
}
